import { useState, useRef } from "react";
import { UserProfile, generateId } from "@/lib/storage";
import { Upload, FileText, CheckCircle, Loader2, X, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

interface Props {
  profile: UserProfile;
  onChange: (profile: UserProfile) => void;
}

export default function ResumeUploadStep({ profile, onChange }: Props) {
  const { user } = useAuth();
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedCounts, setExtractedCounts] = useState({ experience: 0, education: 0, skills: 0 });
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!user) {
      toast.error("You must be logged in to upload a resume.");
      return;
    }

    setFileName(file.name);
    setParsing(true);
    setError(null);

    try {
      // 1. Upload to storage
      const ext = file.name.split(".").pop()?.toLowerCase() || "pdf";
      const storagePath = `${user.id}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("resumes")
        .upload(storagePath, file, { upsert: true });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // 2. Call edge function to parse
      const { data, error: fnError } = await supabase.functions.invoke("parse-resume", {
        body: { filePath: storagePath },
      });

      if (fnError) {
        throw new Error(fnError.message || "Failed to parse resume");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // 3. Merge extracted data into profile
      const updated: UserProfile = { ...profile, personal: { ...profile.personal } };

      // Personal info — only fill empty fields
      if (data.personal) {
        const p = data.personal;
        if (p.fullName && !updated.personal.fullName) updated.personal.fullName = p.fullName;
        if (p.email && !updated.personal.email) updated.personal.email = p.email;
        if (p.phone && !updated.personal.phone) updated.personal.phone = p.phone;
        if (p.location && !updated.personal.location) updated.personal.location = p.location;
        if (p.linkedIn && !updated.personal.linkedIn) updated.personal.linkedIn = p.linkedIn;
        if (p.portfolio && !updated.personal.portfolio) updated.personal.portfolio = p.portfolio;
      }

      // Summary
      if (data.summary && !updated.summary) {
        updated.summary = data.summary;
      }

      // Experience — always overwrite from resume
      if (data.experience?.length > 0) {
        updated.experience = data.experience.map((e: any) => ({
          id: generateId(),
          company: e.company || "",
          title: e.title || "",
          startDate: e.startDate || "",
          endDate: e.endDate || "",
          current: e.current || false,
          description: e.description || "",
        }));
      }

      // Education — always overwrite from resume
      if (data.education?.length > 0) {
        updated.education = data.education.map((e: any) => ({
          id: generateId(),
          institution: e.institution || "",
          degree: e.degree || "",
          field: e.field || "",
          graduationYear: e.graduationYear || "",
        }));
      }

      // Skills — always overwrite from resume
      if (data.skills?.length > 0) {
        updated.skills = data.skills;
      }

      setExtractedCounts({
        experience: updated.experience.length,
        education: updated.education.length,
        skills: updated.skills.length,
      });

      onChange(updated);
      setParsed(true);
      trackEvent("resume_uploaded", { file_type: ext });
      toast.success("Resume parsed successfully!");
    } catch (err: any) {
      console.error("Resume parse error:", err);
      setError(err.message || "Failed to parse resume");
      toast.error(err.message || "Failed to parse resume");
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const clearFile = () => {
    setFileName(null);
    setParsed(false);
    setError(null);
    setExtractedCounts({ experience: 0, education: 0, skills: 0 });
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-bold text-base text-foreground mb-1">Upload Your Resume</h3>
        <p className="text-xs text-muted-foreground">
          Upload your resume and AI will extract your details to prefill the next steps.
        </p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt"
        className="hidden"
        onChange={handleSelect}
      />

      {!fileName ? (
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center gap-3 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
        >
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Upload className="w-6 h-6 text-primary" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground">Drop your resume here</p>
            <p className="text-xs text-muted-foreground mt-1">or click to browse · PDF, DOCX, TXT</p>
          </div>
        </div>
      ) : (
        <div className="border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{fileName}</p>
              {parsing && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> AI is reading your resume…
                </p>
              )}
              {parsed && !error && (
                <p className="text-xs text-primary flex items-center gap-1.5 mt-0.5">
                  <CheckCircle className="w-3 h-3" /> Details extracted & prefilled
                </p>
              )}
              {error && (
                <p className="text-xs text-destructive flex items-center gap-1.5 mt-0.5">
                  <AlertCircle className="w-3 h-3" /> {error}
                </p>
              )}
            </div>
            <button onClick={clearFile} className="text-muted-foreground hover:text-foreground p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          {parsed && !error && (extractedCounts.experience > 0 || extractedCounts.education > 0 || extractedCounts.skills > 0) && (
            <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2 text-[11px] text-muted-foreground">
              {extractedCounts.experience > 0 && (
                <span className="bg-muted px-2 py-0.5 rounded-full">{extractedCounts.experience} experience(s)</span>
              )}
              {extractedCounts.education > 0 && (
                <span className="bg-muted px-2 py-0.5 rounded-full">{extractedCounts.education} education(s)</span>
              )}
              {extractedCounts.skills > 0 && (
                <span className="bg-muted px-2 py-0.5 rounded-full">{extractedCounts.skills} skill(s)</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg bg-muted/50 border border-border p-3">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Optional step.</span> You can skip this and fill in your details manually in the next steps.
        </p>
      </div>
    </div>
  );
}
